(() => {
  if (customElements.get('coffee-recipe')) return;

  // Only gram weights qualify. Pod counts, millilitres and ratio-only recipes
  // must never be interpreted as adjustable doses or beverage weights.
  function grams(text) {
    const value = text.trim().replace(/\u00a0/g, ' ').replace(/[–—−]/g, '-');
    const single = value.match(/^(\d+(?:\.\d+)?)\s*g$/i);
    const range = value.match(/^(\d+(?:\.\d+)?)\s*g?\s*-\s*(\d+(?:\.\d+)?)\s*g$/i);
    const tolerance = value.match(/^(\d+(?:\.\d+)?)\s*g?\s*±\s*(\d+(?:\.\d+)?)\s*g$/i);
    if (single) return Number(single[1]) > 0 ? [Number(single[1])] : null;
    if (tolerance) return Number(tolerance[1]) > Number(tolerance[2]) ? [Number(tolerance[1])] : null;
    if (range) {
      const low = Number(range[1]);
      const high = Number(range[2]);
      return low > 0 && high >= low ? [low, high] : null;
    }
    return null;
  }

  const format = (value) => String(Math.round(value * 10) / 10);
  let sequence = 0;

  customElements.define('coffee-recipe', class extends HTMLElement {
    connectedCallback() {
      // Supports initial pages, theme-editor section replacements and quick views.
      queueMicrotask(() => this.initialize());
    }

    disconnectedCallback() {
      this.closePicker?.();
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;
      const fields = [...this.querySelectorAll('.coffee-desc__recipe-item')];
      const field = (name) => fields.find((item) => item.querySelector('dt')?.textContent.trim().toLowerCase() === name);
      const doseField = field('dose');
      const yieldField = field('extraction yield');
      const doseValue = doseField?.querySelector('dd');
      const yieldValue = yieldField?.querySelector('dd');
      if (!doseValue || !yieldValue) return;
      const originalDose = doseValue.textContent.trim();
      const originalYield = yieldValue.textContent.trim();
      const dose = grams(originalDose);
      const output = grams(originalYield);
      if (!dose || !output) return;

      // Paired ranges use their upper endpoints (e.g. Coffex 20g -> 44g).
      // A tolerance uses the nominal dose; a yield-only range stays a range.
      const baseDose = dose[dose.length - 1];
      const baseOutput = dose.length > 1 ? [output[output.length - 1]] : output;
      if (baseDose < 6 || baseDose > 30) return;
      // A basket selector needs a single starting dose, not a second range
      // option beside its endpoints. Match the initial yield to that dose.
      const initialDose = dose.length > 1 ? `${format(baseDose)}g` : originalDose;
      const initialYield = dose.length > 1 ? `${format(baseOutput[0])}g` : originalYield;
      this.initialized = true;
      doseField.querySelector('dt').textContent = 'Select your dose';
      const id = `coffee-recipe-${++sequence}`;
      const picker = document.createElement('div');
      picker.className = 'coffee-desc__dose-picker';
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'coffee-desc__dose-trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', `${id}-options`);
      const valueLabel = document.createElement('span');
      valueLabel.textContent = initialDose;
      const chevron = document.createElement('span');
      chevron.className = 'coffee-desc__dose-chevron';
      chevron.setAttribute('aria-hidden', 'true');
      trigger.append(valueLabel, chevron);
      trigger.setAttribute('aria-label', `Dose in grams: ${initialDose}`);
      const menu = document.createElement('div');
      menu.id = `${id}-options`;
      menu.className = 'coffee-desc__dose-menu';
      menu.setAttribute('role', 'listbox');
      menu.setAttribute('aria-label', 'Dose in grams');
      menu.hidden = true;
      const choices = [24, 22, 20, 18, 16, 15, 12]
        .filter((value) => initialDose !== `${format(value)}g`)
        .map((value) => ({ value: String(value), label: `${format(value)}g`, weight: value }));
      if (this.dataset.oneKgBag === 'true') {
        choices.push({ value: '7-8', label: '7–8g', weight: 8, doseRange: [7, 8] });
      }
      // The original recipe remains available in its numeric position, not pinned first.
      choices.push({ value: 'original', label: initialDose, weight: baseDose });
      choices.sort((a, b) => b.weight - a.weight);
      const options = choices.map((choice) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'coffee-desc__dose-option';
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', String(choice.value === 'original'));
        option.tabIndex = -1;
        option.dataset.value = choice.value;
        const tick = document.createElement('span');
        tick.className = 'coffee-desc__dose-tick';
        tick.setAttribute('aria-hidden', 'true');
        tick.textContent = '✓';
        const label = document.createElement('span');
        label.textContent = choice.label;
        option.append(tick, label);
        menu.append(option);
        option.addEventListener('click', () => {
          valueLabel.textContent = choice.label;
          trigger.setAttribute('aria-label', `Dose in grams: ${choice.label}`);
          options.forEach((item) => item.setAttribute('aria-selected', String(item === option)));
          if (choice.value === 'original') {
            yieldValue.textContent = initialYield;
          } else if (choice.doseRange) {
            const low = baseOutput[0] * choice.doseRange[0] / baseDose;
            const high = baseOutput[baseOutput.length - 1] * choice.doseRange[1] / baseDose;
            yieldValue.textContent = `${Math.ceil(low)}–${Math.ceil(high)}g`;
          } else {
            yieldValue.textContent = baseOutput.map((weight) => Math.ceil(weight * choice.weight / baseDose)).join('–') + 'g';
          }
          close(true);
        });
        return option;
      });
      const outside = (event) => { if (!picker.contains(event.target)) close(); };
      const close = (restoreFocus = false) => {
        menu.hidden = true;
        picker.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        document.removeEventListener('pointerdown', outside);
        document.removeEventListener('focusin', outside);
        if (restoreFocus) trigger.focus();
      };
      const open = (index = options.findIndex((item) => item.getAttribute('aria-selected') === 'true')) => {
        menu.hidden = false;
        picker.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        document.addEventListener('pointerdown', outside);
        document.addEventListener('focusin', outside);
        options[index].focus();
      };
      this.closePicker = close;
      trigger.addEventListener('click', () => menu.hidden ? open() : close());
      picker.addEventListener('keydown', (event) => {
        const index = options.indexOf(document.activeElement);
        if (event.key === 'Escape' && !menu.hidden) {
          event.preventDefault(); event.stopPropagation(); close(true);
        } else if (event.key === 'Tab' && !menu.hidden) {
          close(true); // Let the browser move from the trigger to the next/previous control.
        } else if (['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          if (event.key === 'Home') open(0);
          else if (event.key === 'End') open(options.length - 1);
          else if (menu.hidden) open();
          else {
            const step = { ArrowDown: 2, ArrowUp: -2, ArrowRight: 1, ArrowLeft: -1 }[event.key];
            const next = index + step;
            if (next >= 0 && next < options.length) open(next);
          }
        }
      });
      picker.append(trigger, menu);
      doseValue.replaceChildren(picker);
      yieldValue.textContent = initialYield;
      yieldValue.setAttribute('aria-live', 'polite');
      yieldValue.setAttribute('aria-atomic', 'true');
    }
  });
})();
